import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { LoginInfoService } from '../login-info.service';
import { LoginInfo, CreateLoginInfoRequest, UpdateLoginInfoRequest } from '../login-info.type';
import { normalizeBackupCode, pastedBackupCodes } from '../../shared/credentials';

export interface LoginInfoFormDialogData {
  mode: 'create' | 'edit';
  loginInfo?: LoginInfo;
  email?: string;
}

@Component({
  selector: 'app-login-info-form-dialog',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  templateUrl: './login-info-form-dialog.html',
  styleUrl: './login-info-form-dialog.scss'
})
export class LoginInfoFormDialog implements OnInit {
  loginInfoForm: FormGroup;
  isEditMode: boolean = false;
  loading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<LoginInfoFormDialog>,
    @Inject(MAT_DIALOG_DATA) public data: LoginInfoFormDialogData,
    private loginInfoService: LoginInfoService,
    private snackBar: MatSnackBar
  ) {
    this.isEditMode = data.mode === 'edit';
    this.loginInfoForm = this.createForm();
  }

  ngOnInit(): void {
    if (this.isEditMode && this.data.loginInfo) {
      this.populateForm(this.data.loginInfo);
    } else if (this.data.email) {
      this.loginInfoForm.patchValue({ email: this.data.email });
    }
  }

  createForm(): FormGroup {
    return this.fb.group({
      email: [
        { value: '', disabled: this.isEditMode },
        [Validators.required, Validators.email]
      ],
      password: ['', this.isEditMode ? [] : [Validators.required]],
      backup_code_1: ['', this.isEditMode ? [] : [Validators.required]],
      backup_code_2: [''],
      backup_code_3: [''],
      backup_code_4: [''],
      backup_code_5: [''],
      backup_code_6: ['']
    });
  }

  populateForm(loginInfo: LoginInfo): void {
    this.loginInfoForm.patchValue({
      email: loginInfo.email
    });
  }

  onSubmit(): void {
    this.trimFormValues();

    if (this.loginInfoForm.invalid) {
      this.snackBar.open('Please fill in all required fields', 'Close', { duration: 3000 });
      return;
    }

    this.loading = true;

    if (this.isEditMode && this.data.loginInfo) {
      this.updateLoginInfo();
    } else {
      this.createLoginInfo();
    }
  }

  trimFormValues(): void {
    const controls = this.loginInfoForm.controls;
    Object.keys(controls).forEach(key => {
      const control = controls[key];
      if (typeof control.value === 'string') {
        const value = key.startsWith('backup_code_') ? normalizeBackupCode(control.value) : control.value.trim();
        control.setValue(value, { emitEvent: false });
      }
    });
  }

  /** A pasted list fills this backup code field and the ones after it. */
  onBackupCodePaste(event: ClipboardEvent, slot: number): void {
    pastedBackupCodes(event).slice(0, 7 - slot).forEach((code, i) => {
      this.loginInfoForm.get(`backup_code_${slot + i}`)?.setValue(code);
    });
  }

  createLoginInfo(): void {
    const formValue = this.loginInfoForm.value;
    const request: CreateLoginInfoRequest = {
      email: formValue.email,
      password: formValue.password,
      backup_code_1: formValue.backup_code_1,
      backup_code_2: formValue.backup_code_2 || undefined,
      backup_code_3: formValue.backup_code_3 || undefined,
      backup_code_4: formValue.backup_code_4 || undefined,
      backup_code_5: formValue.backup_code_5 || undefined,
      backup_code_6: formValue.backup_code_6 || undefined
    };

    this.loginInfoService.createLoginInfo(request).subscribe({
      next: (response) => {
        this.snackBar.open('Login info created successfully', 'Close', { duration: 5000 });
        this.loading = false;
        this.dialogRef.close(response.login_info);
      },
      error: (error) => {
        console.error('Failed to create login info:', error);
        this.snackBar.open(error.error?.error || 'Error creating login info', 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  updateLoginInfo(): void {
    if (!this.data.loginInfo) return;

    const formValue = this.loginInfoForm.value;
    const request: UpdateLoginInfoRequest = {};

    // Only include password if it's been entered
    if (formValue.password && formValue.password.trim()) {
      request.password = formValue.password;
    }

    // Include backup codes if they have values
    if (formValue.backup_code_1 && formValue.backup_code_1.trim()) {
      request.backup_code_1 = formValue.backup_code_1;
    }
    if (formValue.backup_code_2 && formValue.backup_code_2.trim()) {
      request.backup_code_2 = formValue.backup_code_2;
    }
    if (formValue.backup_code_3 && formValue.backup_code_3.trim()) {
      request.backup_code_3 = formValue.backup_code_3;
    }
    if (formValue.backup_code_4 && formValue.backup_code_4.trim()) {
      request.backup_code_4 = formValue.backup_code_4;
    }
    if (formValue.backup_code_5 && formValue.backup_code_5.trim()) {
      request.backup_code_5 = formValue.backup_code_5;
    }
    if (formValue.backup_code_6 && formValue.backup_code_6.trim()) {
      request.backup_code_6 = formValue.backup_code_6;
    }

    this.loginInfoService.updateLoginInfo(this.data.loginInfo.email, request).subscribe({
      next: (response) => {
        this.snackBar.open('Login info updated successfully', 'Close', { duration: 5000 });
        this.loading = false;
        const updatedLoginInfo: LoginInfo = { ...this.data.loginInfo!, ...request };
        this.dialogRef.close(updatedLoginInfo);
      },
      error: (error) => {
        console.error('Failed to update login info:', error);
        this.snackBar.open(error.error?.error || 'Error updating login info', 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
